import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ThemeMode, ResolvedTheme, ThemeTokens, SavedCustomTheme } from '../types/theme';
import { DEFAULT_DARK_TOKENS, THEME_PRESETS } from '../lib/themePresets';
import { hexToRgb, getLuminance } from '../utils/contrast';

export type { ThemeMode, ResolvedTheme, ThemeTokens, SavedCustomTheme };

export interface ThemeContextType {
  theme: ThemeMode;
  resolvedTheme: ResolvedTheme;
  customTokens: ThemeTokens;
  activePresetId: string | null;
  savedCustomThemes: SavedCustomTheme[];
  setTheme: (theme: ThemeMode) => void;
  setCustomTokens: (tokens: ThemeTokens | ((prev: ThemeTokens) => ThemeTokens)) => void;
  applyPreset: (presetId: string) => void;
  saveCustomTheme: (name: string, tokens: ThemeTokens) => SavedCustomTheme;
  deleteCustomTheme: (id: string) => void;
  resetCustomTheme: () => void;
}

const THEME_STORAGE_KEY = 'sahaj_theme_mode';
const CUSTOM_TOKENS_STORAGE_KEY = 'sahaj_custom_theme_tokens';
const ACTIVE_PRESET_STORAGE_KEY = 'sahaj_active_preset_id';
const SAVED_THEMES_STORAGE_KEY = 'sahaj_saved_custom_presets';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/** Convert Hex to HSL string format: "H S% L%" for Tailwind CSS variables */
function hexToHslChannels(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm:
        h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
        break;
      case gNorm:
        h = (bNorm - rNorm) / d + 2;
        break;
      case bNorm:
        h = (rNorm - gNorm) / d + 4;
        break;
    }
    h /= 6;
  }

  const hDeg = Math.round(h * 360);
  const sPct = Math.round(s * 100);
  const lPct = Math.round(l * 100);

  return `${hDeg} ${sPct}% ${lPct}%`;
}

const getInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return 'system';
  try {
    const saved = window.localStorage?.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
    if (saved === 'light' || saved === 'dark' || saved === 'system' || saved === 'custom') {
      return saved;
    }
  } catch {
    // Ignore localStorage errors
  }
  return 'system';
};

const getInitialCustomTokens = (): ThemeTokens => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return DEFAULT_DARK_TOKENS;
  try {
    const saved = window.localStorage.getItem(CUSTOM_TOKENS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_DARK_TOKENS, ...parsed };
    }
  } catch {
    // Ignore
  }
  return DEFAULT_DARK_TOKENS;
};

const getInitialSavedThemes = (): SavedCustomTheme[] => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return [];
  try {
    const saved = window.localStorage.getItem(SAVED_THEMES_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore
  }
  return [];
};

const getInitialPresetId = (): string | null => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_PRESET_STORAGE_KEY);
  } catch {
    return null;
  }
};

const getSystemTheme = (): ResolvedTheme => {
  return 'light';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(getInitialTheme);
  const [customTokens, setCustomTokensState] = useState<ThemeTokens>(getInitialCustomTokens);
  const [activePresetId, setActivePresetId] = useState<string | null>(getInitialPresetId);
  const [savedCustomThemes, setSavedCustomThemes] = useState<SavedCustomTheme[]>(getInitialSavedThemes);

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const initial = getInitialTheme();
    if (initial === 'custom') {
      const tokens = getInitialCustomTokens();
      const rgb = hexToRgb(tokens.appBackground);
      return getLuminance(rgb.r, rgb.g, rgb.b) < 0.5 ? 'dark' : 'light';
    }
    return initial === 'system' ? 'light' : initial;
  });

  // Inject or remove custom CSS variables on documentElement
  const applyCustomTokensToDocument = useCallback((tokens: ThemeTokens) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    // Direct token custom properties
    root.style.setProperty('--theme-app-background', tokens.appBackground);
    root.style.setProperty('--theme-sidebar-background', tokens.primarySidebarBackground);
    root.style.setProperty('--theme-sidebar-mini-background', tokens.miniSidebarBackground);
    root.style.setProperty('--theme-header-background', tokens.headerBackground);
    root.style.setProperty('--theme-sidebar-text', tokens.sidebarText);
    root.style.setProperty('--theme-sidebar-secondary-text', tokens.sidebarSecondaryText);
    root.style.setProperty('--theme-sidebar-active-background', tokens.sidebarActiveBackground);
    root.style.setProperty('--theme-sidebar-active-text', tokens.sidebarActiveText);
    root.style.setProperty('--theme-sidebar-hover-background', tokens.sidebarHoverBackground);
    root.style.setProperty('--theme-sidebar-border', tokens.sidebarBorder);

    root.style.setProperty('--theme-chat-background', tokens.chatBackground);
    root.style.setProperty('--theme-message-text', tokens.messageText);
    root.style.setProperty('--theme-message-secondary-text', tokens.messageSecondaryText);
    root.style.setProperty('--theme-message-hover', tokens.messageHoverBackground);
    root.style.setProperty('--theme-incoming-message-background', tokens.incomingMessageBackground);
    root.style.setProperty('--theme-outgoing-message-background', tokens.outgoingMessageBackground);
    root.style.setProperty('--theme-message-timestamp', tokens.messageTimestamp);
    root.style.setProperty('--theme-thread-background', tokens.threadBackground);
    root.style.setProperty('--theme-composer-background', tokens.composerBackground);
    root.style.setProperty('--theme-composer-border', tokens.composerBorder);
    root.style.setProperty('--theme-composer-text', tokens.composerText);
    root.style.setProperty('--theme-composer-placeholder', tokens.composerPlaceholder);

    root.style.setProperty('--theme-primary', tokens.primaryAccent);
    root.style.setProperty('--theme-primary-hover', tokens.primaryButtonHover);
    root.style.setProperty('--theme-primary-button-background', tokens.primaryButtonBackground);
    root.style.setProperty('--theme-primary-button-text', tokens.primaryButtonText);
    root.style.setProperty('--theme-secondary-button-background', tokens.secondaryButtonBackground);
    root.style.setProperty('--theme-secondary-button-text', tokens.secondaryButtonText);
    root.style.setProperty('--theme-icon-color', tokens.iconColor);
    root.style.setProperty('--theme-icon-hover-color', tokens.iconHoverColor);
    root.style.setProperty('--theme-link-color', tokens.linkColor);
    root.style.setProperty('--theme-focus-ring', tokens.focusRing);
    root.style.setProperty('--theme-selected-state', tokens.selectedState);
    root.style.setProperty('--theme-active-state', tokens.activeState);

    root.style.setProperty('--theme-mention-background', tokens.mentionBackground);
    root.style.setProperty('--theme-mention-text', tokens.mentionText);
    root.style.setProperty('--theme-reaction-background', tokens.reactionBackground);
    root.style.setProperty('--theme-reaction-active-background', tokens.reactionActiveBackground);
    root.style.setProperty('--theme-reaction-active-border', tokens.reactionActiveBorder);
    root.style.setProperty('--theme-reaction-border', tokens.reactionBorder);

    root.style.setProperty('--theme-card-background', tokens.cardBackground);
    root.style.setProperty('--theme-modal-background', tokens.modalBackground);
    root.style.setProperty('--theme-dropdown-background', tokens.dropdownBackground);
    root.style.setProperty('--theme-popover-background', tokens.popoverBackground);
    root.style.setProperty('--theme-tooltip-background', tokens.tooltipBackground);
    root.style.setProperty('--theme-tooltip-text', tokens.tooltipText);
    root.style.setProperty('--theme-border', tokens.borderColor);
    root.style.setProperty('--theme-divider', tokens.dividerColor);
    root.style.setProperty('--theme-input-background', tokens.inputBackground);
    root.style.setProperty('--theme-input-border', tokens.inputBorder);
    root.style.setProperty('--theme-input-focus-border', tokens.inputFocusBorder);

    root.style.setProperty('--theme-success', tokens.success);
    root.style.setProperty('--theme-warning', tokens.warning);
    root.style.setProperty('--theme-error', tokens.error);
    root.style.setProperty('--theme-info', tokens.info);
    root.style.setProperty('--theme-status-online', tokens.statusOnline);
    root.style.setProperty('--theme-status-away', tokens.statusAway);
    root.style.setProperty('--theme-status-offline', tokens.statusOffline);

    root.style.setProperty('--theme-code-block-background', tokens.codeBlockBackground);
    root.style.setProperty('--theme-code-block-text', tokens.codeBlockText);
    root.style.setProperty('--theme-inline-code-background', tokens.inlineCodeBackground);
    root.style.setProperty('--theme-inline-code-text', tokens.inlineCodeText);
    root.style.setProperty('--theme-syntax-keyword', tokens.syntaxKeyword);
    root.style.setProperty('--theme-syntax-string', tokens.syntaxString);
    root.style.setProperty('--theme-syntax-function', tokens.syntaxFunction);
    root.style.setProperty('--theme-syntax-comment', tokens.syntaxComment);

    // Map to Tailwind CSS base variables (in HSL format) so existing components adapt instantly
    root.style.setProperty('--background', hexToHslChannels(tokens.appBackground));
    root.style.setProperty('--foreground', hexToHslChannels(tokens.messageText));
    root.style.setProperty('--card', hexToHslChannels(tokens.cardBackground));
    root.style.setProperty('--card-foreground', hexToHslChannels(tokens.messageText));
    root.style.setProperty('--popover', hexToHslChannels(tokens.popoverBackground));
    root.style.setProperty('--popover-foreground', hexToHslChannels(tokens.messageText));
    root.style.setProperty('--primary', hexToHslChannels(tokens.primaryAccent));
    root.style.setProperty('--primary-foreground', hexToHslChannels(tokens.primaryButtonText));
    root.style.setProperty('--secondary', hexToHslChannels(tokens.secondaryButtonBackground));
    root.style.setProperty('--secondary-foreground', hexToHslChannels(tokens.secondaryButtonText));
    root.style.setProperty('--muted', hexToHslChannels(tokens.messageHoverBackground));
    root.style.setProperty('--muted-foreground', hexToHslChannels(tokens.messageSecondaryText));
    root.style.setProperty('--accent', hexToHslChannels(tokens.selectedState));
    root.style.setProperty('--accent-foreground', hexToHslChannels(tokens.primaryAccent));
    root.style.setProperty('--destructive', hexToHslChannels(tokens.error));
    root.style.setProperty('--destructive-foreground', '0 0% 100%');
    root.style.setProperty('--border', hexToHslChannels(tokens.borderColor));
    root.style.setProperty('--input', hexToHslChannels(tokens.inputBorder));
    root.style.setProperty('--ring', hexToHslChannels(tokens.focusRing));

    root.style.setProperty('--sidebar', hexToHslChannels(tokens.primarySidebarBackground));
    root.style.setProperty('--sidebar-foreground', hexToHslChannels(tokens.sidebarText));
    root.style.setProperty('--sidebar-border', hexToHslChannels(tokens.sidebarBorder));
    root.style.setProperty('--sidebar-accent', hexToHslChannels(tokens.sidebarActiveBackground));
    root.style.setProperty('--sidebar-accent-foreground', hexToHslChannels(tokens.sidebarActiveText));
  }, []);

  const removeCustomTokensFromDocument = useCallback(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    const propsToRemove = [
      '--theme-app-background',
      '--theme-sidebar-background',
      '--theme-sidebar-mini-background',
      '--theme-header-background',
      '--theme-sidebar-text',
      '--theme-sidebar-secondary-text',
      '--theme-sidebar-active-background',
      '--theme-sidebar-active-text',
      '--theme-sidebar-hover-background',
      '--theme-sidebar-border',
      '--theme-chat-background',
      '--theme-message-text',
      '--theme-message-secondary-text',
      '--theme-message-hover',
      '--theme-incoming-message-background',
      '--theme-outgoing-message-background',
      '--theme-message-timestamp',
      '--theme-thread-background',
      '--theme-composer-background',
      '--theme-composer-border',
      '--theme-composer-text',
      '--theme-composer-placeholder',
      '--theme-primary',
      '--theme-primary-hover',
      '--theme-primary-button-background',
      '--theme-primary-button-text',
      '--theme-secondary-button-background',
      '--theme-secondary-button-text',
      '--theme-icon-color',
      '--theme-icon-hover-color',
      '--theme-link-color',
      '--theme-focus-ring',
      '--theme-selected-state',
      '--theme-active-state',
      '--theme-mention-background',
      '--theme-mention-text',
      '--theme-reaction-background',
      '--theme-reaction-active-background',
      '--theme-reaction-active-border',
      '--theme-reaction-border',
      '--theme-card-background',
      '--theme-modal-background',
      '--theme-dropdown-background',
      '--theme-popover-background',
      '--theme-tooltip-background',
      '--theme-tooltip-text',
      '--theme-border',
      '--theme-divider',
      '--theme-input-background',
      '--theme-input-border',
      '--theme-input-focus-border',
      '--theme-success',
      '--theme-warning',
      '--theme-error',
      '--theme-info',
      '--theme-status-online',
      '--theme-status-away',
      '--theme-status-offline',
      '--theme-code-block-background',
      '--theme-code-block-text',
      '--theme-inline-code-background',
      '--theme-inline-code-text',
      '--theme-syntax-keyword',
      '--theme-syntax-string',
      '--theme-syntax-function',
      '--theme-syntax-comment',
      // Base variables to reset to CSS defaults
      '--background',
      '--foreground',
      '--card',
      '--card-foreground',
      '--popover',
      '--popover-foreground',
      '--primary',
      '--primary-foreground',
      '--secondary',
      '--secondary-foreground',
      '--muted',
      '--muted-foreground',
      '--accent',
      '--accent-foreground',
      '--destructive',
      '--destructive-foreground',
      '--border',
      '--input',
      '--ring',
      '--sidebar',
      '--sidebar-foreground',
      '--sidebar-border',
      '--sidebar-accent',
      '--sidebar-accent-foreground',
    ];

    propsToRemove.forEach((p) => root.style.removeProperty(p));
  }, []);

  const applyTheme = useCallback(
    (mode: ThemeMode, resolved: ResolvedTheme, tokens: ThemeTokens) => {
      if (typeof document === 'undefined') return;
      const root = document.documentElement;

      if (mode === 'custom') {
        applyCustomTokensToDocument(tokens);
        const rgb = hexToRgb(tokens.appBackground);
        const isDark = getLuminance(rgb.r, rgb.g, rgb.b) < 0.5;
        if (isDark) {
          root.classList.add('dark');
          root.style.colorScheme = 'dark';
        } else {
          root.classList.remove('dark');
          root.style.colorScheme = 'light';
        }
      } else {
        removeCustomTokensFromDocument();
        if (resolved === 'dark') {
          root.classList.add('dark');
          root.style.colorScheme = 'dark';
        } else {
          root.classList.remove('dark');
          root.style.colorScheme = 'light';
        }
      }
    },
    [applyCustomTokensToDocument, removeCustomTokensFromDocument]
  );

  const setTheme = useCallback(
    (newTheme: ThemeMode) => {
      setThemeState(newTheme);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        }
      } catch {
        // Ignore
      }

      let resolved: ResolvedTheme;
      if (newTheme === 'custom') {
        const rgb = hexToRgb(customTokens.appBackground);
        resolved = getLuminance(rgb.r, rgb.g, rgb.b) < 0.5 ? 'dark' : 'light';
      } else if (newTheme === 'system') {
        resolved = getSystemTheme();
      } else {
        resolved = newTheme;
      }

      setResolvedTheme(resolved);
      applyTheme(newTheme, resolved, customTokens);
    },
    [customTokens, applyTheme]
  );

  const setCustomTokens = useCallback(
    (updater: ThemeTokens | ((prev: ThemeTokens) => ThemeTokens)) => {
      setCustomTokensState((prev) => {
        const nextTokens = typeof updater === 'function' ? updater(prev) : updater;
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(CUSTOM_TOKENS_STORAGE_KEY, JSON.stringify(nextTokens));
          }
        } catch {
          // Ignore
        }

        if (theme === 'custom') {
          const rgb = hexToRgb(nextTokens.appBackground);
          const resolved = getLuminance(rgb.r, rgb.g, rgb.b) < 0.5 ? 'dark' : 'light';
          setResolvedTheme(resolved);
          applyTheme('custom', resolved, nextTokens);
        }

        return nextTokens;
      });
    },
    [theme, applyTheme]
  );

  const applyPreset = useCallback(
    (presetId: string) => {
      const found = THEME_PRESETS.find((p) => p.id === presetId);
      if (!found) return;

      setActivePresetId(presetId);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(ACTIVE_PRESET_STORAGE_KEY, presetId);
        }
      } catch {
        // Ignore
      }

      setCustomTokens(found.tokens);
      if (theme !== 'custom') {
        setTheme('custom');
      }
    },
    [setCustomTokens, setTheme, theme]
  );

  const saveCustomTheme = useCallback(
    (name: string, tokens: ThemeTokens): SavedCustomTheme => {
      const newSaved: SavedCustomTheme = {
        id: `custom-${Date.now()}`,
        name: name.trim() || 'My Custom Theme',
        createdAt: Date.now(),
        tokens: { ...tokens },
      };

      setSavedCustomThemes((prev) => {
        const next = [newSaved, ...prev];
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(SAVED_THEMES_STORAGE_KEY, JSON.stringify(next));
          }
        } catch {
          // Ignore
        }
        return next;
      });

      return newSaved;
    },
    []
  );

  const deleteCustomTheme = useCallback((id: string) => {
    setSavedCustomThemes((prev) => {
      const next = prev.filter((t) => t.id !== id);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(SAVED_THEMES_STORAGE_KEY, JSON.stringify(next));
        }
      } catch {
        // Ignore
      }
      return next;
    });
  }, []);

  const resetCustomTheme = useCallback(() => {
    const defaultTokens = DEFAULT_DARK_TOKENS;
    setActivePresetId('defaultDark');
    setCustomTokens(defaultTokens);
  }, [setCustomTokens]);

  // Initial theme application and system preference observer
  useEffect(() => {
    if (theme === 'custom') {
      const rgb = hexToRgb(customTokens.appBackground);
      const res = getLuminance(rgb.r, rgb.g, rgb.b) < 0.5 ? 'dark' : 'light';
      setResolvedTheme(res);
      applyTheme('custom', res, customTokens);
      return;
    }

    const currentResolved: ResolvedTheme = theme === 'dark' ? 'dark' : 'light';
    setResolvedTheme(currentResolved);
    applyTheme(theme, currentResolved, customTokens);
  }, [theme, customTokens, applyTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        customTokens,
        activePresetId,
        savedCustomThemes,
        setTheme,
        setCustomTokens,
        applyPreset,
        saveCustomTheme,
        deleteCustomTheme,
        resetCustomTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
